import { useEffect, useRef, useCallback, useState } from "react";

export interface WsBatchItem {
  item_id: string;
  item_type: "report" | "folder" | "summary";
  org_wrapped_dek: string;
}

export interface WsProgress {
  total: number;
  completed: number;
  failed: number;
}

export interface WsItemsMessage {
  type: "items";
  batch_id: string;
  items: WsBatchItem[];
  total: number;
  completed: number;
  failed: number;
  has_more: boolean;
}

export interface WsProgressMessage {
  type: "progress" | "complete";
  batch_id: string;
  status: string;
  total: number;
  completed: number;
  failed: number;
}

export type WsMessage = WsItemsMessage | WsProgressMessage | { type: "error"; message: string };

/**
 * Single bidirectional WebSocket for the re-encryption streaming flow.
 *
 * Protocol:
 * 1. On connect, server pushes first page of items (up to 100)
 * 2. Client processes items, POSTs /reencryption-chunk
 * 3. Client sends { type: "next" } to get the next page
 * 4. Repeat until has_more = false or type = "complete"
 * 5. Client can send { type: "ping" } for progress-only updates
 */
export function useReencryptionWs(batchId: string | null) {
  const [progress, setProgress] = useState<WsProgress>({ total: 0, completed: 0, failed: 0 });
  const [isComplete, setIsComplete] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const onItemsRef = useRef<((msg: WsItemsMessage) => void) | null>(null);

  const connect = useCallback(() => {
    if (!batchId) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const isDev = import.meta.env.MODE === "development";
    const baseUrl = isDev
      ? import.meta.env.VITE_REPORTS_API_URL?.replace(/^https?:/, protocol) ?? `${protocol}//localhost:8000`
      : `${protocol}//${window.location.host}`;
    const wsUrl = `${baseUrl}/reports/ws/reencryption-status/${batchId}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => setIsConnected(true);

    ws.onmessage = (event) => {
      try {
        const data: WsMessage = JSON.parse(event.data);

        if (data.type === "error") {
          console.error("[useReencryptionWs] Server error:", data.message);
          return;
        }

        if (data.type === "items") {
          setProgress({ total: data.total, completed: data.completed, failed: data.failed });
          onItemsRef.current?.(data);
        }

        if (data.type === "progress") {
          setProgress({ total: data.total, completed: data.completed, failed: data.failed });
        }

        if (data.type === "complete") {
          setProgress({ total: data.total, completed: data.completed, failed: data.failed });
          setIsComplete(true);
        }
      } catch {
        console.warn("[useReencryptionWs] Failed to parse WS message");
      }
    };

    ws.onclose = () => {
      wsRef.current = null;
      setIsConnected(false);
    };
  }, [batchId]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000);
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  /** Request the next page of items from the server */
  const requestNextPage = useCallback(() => {
    wsRef.current?.send(JSON.stringify({ type: "next" }));
  }, []);

  /** Request a progress-only update (no items) */
  const requestProgress = useCallback(() => {
    wsRef.current?.send(JSON.stringify({ type: "ping" }));
  }, []);

  /** Register a callback for when items arrive */
  const onItems = useCallback((cb: (msg: WsItemsMessage) => void) => {
    onItemsRef.current = cb;
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return { progress, isComplete, isConnected, requestNextPage, requestProgress, onItems, disconnect };
}
